import {
  DndContext,
  DragEndEvent,
  useSensor,
  useSensors,
  PointerSensor,
} from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import useInboxState from '@modules/InboxModule/hooks/useInboxState';
import { inboxActions } from '@modules/InboxModule/state';

interface DroppableProps {
  children: React.ReactNode | React.ReactNode[];
}

/**
 * Wraps child components in a container that allows them to be draggable.
 * Provides a context for the draggable items to share their positions relative to their initial position.

 */
const Droppable = ({ children }: DroppableProps) => {
  const { callBarPositionDeltas } = useInboxState();
  const { setCallBarPositionDeltas } = inboxActions;

  // add pointer sensor so that we can test it with cypress: more on sensors: https://docs.dndkit.com/api-documentation/sensors
  const sensors = useSensors(useSensor(PointerSensor));

  /**
   * Handles the drag end event and updates the positions of draggable items.
   *
   * @param {DragEndEvent} event - The drag end event object.
   */
  const handleDragEnd = (event: DragEndEvent) => {
    const { id } = event.active;
    const { x, y } = event.delta;

    let updatedCallBarPositionDeltas;

    if (!callBarPositionDeltas[id]) {
      updatedCallBarPositionDeltas = {
        ...callBarPositionDeltas,
        [id]: {
          x,
          y,
        },
      };
    } else {
      updatedCallBarPositionDeltas = {
        ...callBarPositionDeltas,
        [id]: {
          x: callBarPositionDeltas[id].x + x,
          y: callBarPositionDeltas[id].y + y,
        },
      };
    }
    setCallBarPositionDeltas(updatedCallBarPositionDeltas);
  };

  return (
    <DndContext
      sensors={sensors}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToParentElement]}
      autoScroll={false}
    >
      <div className='position-relative h-100'>{children}</div>
    </DndContext>
  );
};

export default Droppable;
